/**
 * Admin screen for Section 17 of an MRE: maps ADRs/Lights and their questions to an MRE,
 * stores per-MRE placeholder (part number) values, and uploads one photo per question slot.
 */
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../context/AuthContext';
import { Autocomplete, TextField, CircularProgress, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, List, ListItem, ListItemText, Grid } from '@mui/material';
import { PhotoIcon, XMarkIcon, ArrowUpTrayIcon, TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB per file
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp'
];
const REPORTS_BUCKET = 'reports';
const SECTION_17_FOLDER = 'SECTION 17 PHOTOS';
const PHOTO_SIGNED_URL_TTL_SECONDS = 3600;

const decodeStoragePath = (path) => {
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

// Stored photo_url may be a legacy public URL, a signed URL, or the raw storage path.
const toReportsStoragePath = (photoUrl) => {
  if (!photoUrl) return null;

  const withoutQuery = String(photoUrl).split('?')[0];
  const markers = [
    `/object/public/${REPORTS_BUCKET}/`,
    `/object/sign/${REPORTS_BUCKET}/`,
    `/object/authenticated/${REPORTS_BUCKET}/`,
  ];

  for (const marker of markers) {
    const idx = withoutQuery.indexOf(marker);
    if (idx !== -1) {
      return decodeStoragePath(withoutQuery.slice(idx + marker.length));
    }
  }

  if (/^https?:\/\//i.test(withoutQuery)) {
    return null;
  }

  return decodeStoragePath(withoutQuery);
};

const buildSection17FilePath = (mreCode, category, questionId, fileExtension) => {
  const prefix = category === 'adr' ? 'q' : 'lq';
  return `${mreCode}/${SECTION_17_FOLDER}/${prefix}_${questionId}.${fileExtension}`;
};

const createReportsSignedUrl = async (filePath) => {
  const { data, error } = await supabase.storage
    .from(REPORTS_BUCKET)
    .createSignedUrl(filePath, PHOTO_SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
};

const Section17PhotosUpload = () => {
  const [mres, setMres] = useState([]);
  const [adrs, setAdrs] = useState([]);
  const [lights, setLights] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [lightQuestions, setLightQuestions] = useState([]);
  
  const [selectedMre, setSelectedMre] = useState(null);
  const [category, setCategory] = useState(''); // 'adr' or 'light'
  const [selectedAdr, setSelectedAdr] = useState(null);
  const [selectedLight, setSelectedLight] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedLightQuestion, setSelectedLightQuestion] = useState(null);
  
  const [loadingMres, setLoadingMres] = useState(true);
  const [loadingAdrs, setLoadingAdrs] = useState(false);
  const [loadingLights, setLoadingLights] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingLightQuestions, setLoadingLightQuestions] = useState(false);

  // Modal states
  const [showAdrModal, setShowAdrModal] = useState(false);
  const [showLightModal, setShowLightModal] = useState(false);
  const [showAdrQuestionModal, setShowAdrQuestionModal] = useState(false);
  const [showLightQuestionModal, setShowLightQuestionModal] = useState(false);
  const [allAdrs, setAllAdrs] = useState([]);
  const [allLights, setAllLights] = useState([]);
  const [loadingAllAdrs, setLoadingAllAdrs] = useState(false);
  const [loadingAllLights, setLoadingAllLights] = useState(false);
  const [selectedNewAdr, setSelectedNewAdr] = useState('');
  const [selectedNewLight, setSelectedNewLight] = useState('');
  const [selectedNewQuestion, setSelectedNewQuestion] = useState('');
  const [selectedNewLightQuestion, setSelectedNewLightQuestion] = useState('');
  const [deletingAdr, setDeletingAdr] = useState(null);
  const [deletingLight, setDeletingLight] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(null);
  const [deletingLightQuestion, setDeletingLightQuestion] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editedQuestionText, setEditedQuestionText] = useState('');
  const [editingLightQuestionId, setEditingLightQuestionId] = useState(null);
  const [editedLightQuestionText, setEditedLightQuestionText] = useState('');
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [savingLightQuestion, setSavingLightQuestion] = useState(false);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [savingPlaceholders, setSavingPlaceholders] = useState(false);
  const [lightPlaceholderValues, setLightPlaceholderValues] = useState({});
  const [savingLightPlaceholders, setSavingLightPlaceholders] = useState(false);

  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch all MREs on component mount
  useEffect(() => {
    fetchMres();
  }, []);

  // Fetch ADRs when MRE is selected
  useEffect(() => {
    if (selectedMre) {
      setCategory(''); // Reset category when MRE changes
      setSelectedAdr(null);
      setSelectedLight(null);
      setSelectedQuestion(null);
      setSelectedLightQuestion(null);
      setAdrs([]);
      setLights([]);
      setQuestions([]);
      setLightQuestions([]);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setCategory('');
      setAdrs([]);
      setLights([]);
      setSelectedAdr(null);
      setSelectedLight(null);
      setSelectedQuestion(null);
      setSelectedLightQuestion(null);
      setQuestions([]);
      setLightQuestions([]);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [selectedMre]);

  // Fetch ADRs or Lights based on category selection
  useEffect(() => {
    if (selectedMre && category === 'adr') {
      fetchAdrs(selectedMre.id);
      setSelectedAdr(null);
      setSelectedQuestion(null);
      setQuestions([]);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else if (selectedMre && category === 'light') {
      fetchLights(selectedMre.id);
      setSelectedLight(null);
      setSelectedLightQuestion(null);
      setLightQuestions([]);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [category]);

  // Fetch questions when ADR is selected
  useEffect(() => {
    if (selectedAdr) {
      fetchQuestions(selectedAdr.id);
      setSelectedQuestion(null);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setQuestions([]);
      setSelectedQuestion(null);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [selectedAdr]);

  // Fetch light questions when Light is selected
  useEffect(() => {
    if (selectedLight) {
      fetchLightQuestions(selectedLight.id);
      setSelectedLightQuestion(null);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setLightQuestions([]);
      setSelectedLightQuestion(null);
      // Reset form
      setPhoto(null);
      setUploadedPhoto(null);
      setError(null);
      setSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [selectedLight]);

  // Fetch existing photo when question is selected
  useEffect(() => {
    // Reset form when question changes
    setPhoto(null);
    setUploadedPhoto(null);
    setError(null);
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (selectedMre && (selectedQuestion || selectedLightQuestion)) {
      fetchExistingPhoto();
    } else {
      setUploadedPhoto(null);
    }
  }, [selectedQuestion, selectedLightQuestion, selectedMre]);

  // mre_id is the human-readable report code (e.g. MRE001) and doubles as the storage folder name,
  // while id is the surrogate key used by every mapping table.
  const fetchMres = async () => {
    try {
      setLoadingMres(true);
      const { data, error } = await supabase
        .from('mre_checklist')
        .select('id, mre_id')
        .order('mre_id', { ascending: true });

      if (error) throw error;
      
      setMres(data || []);
    } catch (error) {
      console.error('Error fetching MREs:', error);
    } finally {
      setLoadingMres(false);
    }
  };

  // ADRs reach an MRE only through the many-to-many mapping table, so this is a two-step read:
  // collect the mapped ids first, then load their details.
  const fetchAdrs = async (mreId) => {
    try {
      setLoadingAdrs(true);
      
      // First get the mre_adr_mapping to find related ADRs
      const { data: mappingData, error: mappingError } = await supabase
        .from('mre_adr_mapping')
        .select('adr_checklist_id')
        .eq('mre_checklist_id', mreId);

      if (mappingError) throw mappingError;

      if (mappingData && mappingData.length > 0) {
        const adrIds = mappingData.map(item => item.adr_checklist_id);
        
        // Fetch ADR details
        const { data: adrData, error: adrError } = await supabase
          .from('adr_checklist')
          .select('id, adr_no')
          .in('id', adrIds)
          .order('adr_no', { ascending: true });

        if (adrError) throw adrError;
        
        setAdrs(adrData || []);
      } else {
        setAdrs([]);
      }
    } catch (error) {
      console.error('Error fetching ADRs:', error);
    } finally {
      setLoadingAdrs(false);
    }
  };

  const fetchQuestions = async (adrId) => {
    try {
      setLoadingQuestions(true);
      
      // The mapping table is keyed by MRE, so without one there is nothing to query.
      if (!selectedMre) {
        setQuestions([]);
        return;
      }

      // Get questions through the mapping table - filtered by MRE
      // This allows the same question to exist in different MREs with different placeholder values
      // Each MRE-question combination can have unique values stored in section_17_part_numbers
      const { data: mappingData, error: mappingError } = await supabase
        .from('mre_adr_questions_mapping')
        .select(`
          question_id,
          adr_questions(
            id,
            questions,
            adr_checklist_id
          )
        `)
        .eq('mre_id', selectedMre.id);

      if (mappingError) throw mappingError;

      if (mappingData && mappingData.length > 0) {
        // Filter questions for the selected ADR and flatten the structure
        // This ensures same question can exist in different MREs with different placeholder values
        // The mapping row only holds mre_id + question_id, so narrowing to one ADR has to be
        // done here using the joined adr_checklist_id.
        const filteredQuestions = mappingData
          .filter(item => item.adr_questions?.adr_checklist_id === adrId)
          .map(item => ({
            id: item.adr_questions.id,
            questions: item.adr_questions.questions
          }));
        
        setQuestions(filteredQuestions || []);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchLights = async (mreId) => {
    try {
      setLoadingLights(true);
      
      // First get the mre_light_mapping to find related Lights
      const { data: mappingData, error: mappingError } = await supabase
        .from('mre_light_mapping')
        .select('light_checklist_id')
        .eq('mre_checklist_id', mreId);

      if (mappingError) throw mappingError;

      if (mappingData && mappingData.length > 0) {
        const lightIds = mappingData.map(item => item.light_checklist_id);
        
        // Fetch Light details
        const { data: lightData, error: lightError } = await supabase
          .from('light_checklist')
          .select('id, light_model')
          .in('id', lightIds)
          .order('light_model', { ascending: true });

        if (lightError) throw lightError;
        
        setLights(lightData || []);
      } else {
        setLights([]);
      }
    } catch (error) {
      console.error('Error fetching lights:', error);
    } finally {
      setLoadingLights(false);
    }
  };

  // Full catalogue (including ADRs not mapped to any MRE) used by the management modal.
  const fetchAllAdrs = async () => {
    try {
      setLoadingAllAdrs(true);
      const { data, error } = await supabase
        .from('adr_checklist')
        .select('id, adr_no')
        .order('adr_no', { ascending: true });

      if (error) throw error;
      setAllAdrs(data || []);
    } catch (error) {
      console.error('Error fetching all ADRs:', error);
    } finally {
      setLoadingAllAdrs(false);
    }
  };

  const fetchAllLights = async () => {
    try {
      setLoadingAllLights(true);
      const { data, error } = await supabase
        .from('light_checklist')
        .select('id, light_model')
        .order('light_model', { ascending: true });

      if (error) throw error;
      setAllLights(data || []);
    } catch (error) {
      console.error('Error fetching all lights:', error);
    } finally {
      setLoadingAllLights(false);
    }
  };

  // The catalogue is only loaded when the modal is opened, not on page load.
  const handleOpenAdrModal = () => {
    setShowAdrModal(true);
    fetchAllAdrs();
  };

  const handleOpenLightModal = () => {
    setShowLightModal(true);
    fetchAllLights();
  };

  const handleCloseAdrModal = () => {
    setShowAdrModal(false);
    setSelectedNewAdr('');
  };

  const handleCloseLightModal = () => {
    setShowLightModal(false);
    setSelectedNewLight('');
  };

  // Maps a typed ADR number to this MRE, creating the adr_checklist row first if the
  // number is not already in the catalogue.
  const handleAddAdr = async () => {
    if (!selectedMre || !selectedNewAdr.trim()) return;

    try {
      const adrInput = selectedNewAdr.trim();
      
      // Normalize input for better matching (case insensitive, trim spaces)
      const normalizedInput = adrInput.toLowerCase().replace(/\s+/g, ' ');
      
      // Try to find ADR with case-insensitive and normalized matching
      const { data: allAdrData, error: fetchError } = await supabase
        .from('adr_checklist')
        .select('id, adr_no');

      if (fetchError) throw fetchError;

      // Find matching ADR with flexible matching
      let matchedAdr = allAdrData?.find(adr => {
        const normalized = adr.adr_no.toLowerCase().replace(/\s+/g, ' ');
        // Exact match
        if (normalized === normalizedInput) return true;
        
        // Handle number format variations (e.g., "2/00" vs "02/00")
        const inputParts = normalizedInput.split('/');
        const adrParts = normalized.split('/');
        
        // Only the leading number is compared numerically; the suffix after "/" must match exactly.
        if (inputParts.length === 2 && adrParts.length === 2) {
          const inputNum = parseInt(inputParts[0]);
          const adrNum = parseInt(adrParts[0]);
          return !isNaN(inputNum) && !isNaN(adrNum) && inputNum === adrNum && inputParts[1] === adrParts[1];
        }
        
        return false;
      });

      let adrId;

      // If ADR doesn't exist, create it
      if (!matchedAdr) {
        const { data: newAdr, error: createError } = await supabase
          .from('adr_checklist')
          .insert([{ adr_no: adrInput }])
          .select('id')
          .single();

        if (createError) throw createError;
        
        adrId = newAdr.id;
        alert(`New ADR "${adrInput}" created successfully!`);
      } else {
        adrId = matchedAdr.id;
      }

      // Check if mapping already exists
      // .single() errors when there is no row, so the error is ignored and only data is checked.
      const { data: existing } = await supabase
        .from('mre_adr_mapping')
        .select('id')
        .eq('mre_checklist_id', selectedMre.id)
        .eq('adr_checklist_id', adrId)
        .single();

      if (existing) {
        alert('This ADR is already mapped to this MRE.');
        return;
      }

      // Insert new mapping
      const { error } = await supabase
        .from('mre_adr_mapping')
        .insert([{
          mre_checklist_id: selectedMre.id,
          adr_checklist_id: adrId
        }]);

      if (error) throw error;

      // Refresh ADRs list
      await fetchAdrs(selectedMre.id);
      setSelectedNewAdr('');
      alert(matchedAdr ? 'ADR mapped successfully!' : 'ADR created and mapped successfully!');
    } catch (error) {
      console.error('Error adding ADR:', error);
      alert('Failed to add ADR. Please try again.');
    }
  };

  // Removes only the MRE-to-ADR mapping; the adr_checklist row stays because other MREs may use it.
  const handleDeleteAdr = async (adrId) => {
    if (!selectedMre || !window.confirm('Are you sure you want to remove this ADR from this MRE?')) return;

    try {
      setDeletingAdr(adrId);
      const { error } = await supabase
        .from('mre_adr_mapping')
        .delete()
        .eq('mre_checklist_id', selectedMre.id)
        .eq('adr_checklist_id', adrId);

      if (error) throw error;

      // Refresh ADRs list
      await fetchAdrs(selectedMre.id);
      alert('ADR removed successfully!');
    } catch (error) {
      console.error('Error deleting ADR:', error);
      alert('Failed to remove ADR. Please try again.');
    } finally {
      setDeletingAdr(null);
    }
  };

  const handleAddLight = async () => {
    if (!selectedMre || !selectedNewLight.trim()) return;

    try {
      const lightInput = selectedNewLight.trim();
      
      // Normalize input for better matching (case insensitive, trim spaces)
      const normalizedInput = lightInput.toLowerCase().replace(/\s+/g, ' ');
      
      // Try to find Light with case-insensitive and normalized matching
      const { data: allLightData, error: fetchError } = await supabase
        .from('light_checklist')
        .select('id, light_model');

      if (fetchError) throw fetchError;

      // Find matching Light with flexible matching
      // Light models have no numeric convention, so matching is exact once normalized
      // (unlike ADR numbers, which tolerate 2/00 vs 02/00).
      let matchedLight = allLightData?.find(light => {
        const normalized = light.light_model.toLowerCase().replace(/\s+/g, ' ');
        return normalized === normalizedInput;
      });

      let lightId;

      // If Light doesn't exist, create it
      if (!matchedLight) {
        const { data: newLight, error: createError } = await supabase
          .from('light_checklist')
          .insert([{ 
            light_model: lightInput,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select('id')
          .single();

        if (createError) throw createError;
        
        lightId = newLight.id;
        alert(`New Light "${lightInput}" created successfully!`);
      } else {
        lightId = matchedLight.id;
      }

      // Check if mapping already exists
      const { data: existing } = await supabase
        .from('mre_light_mapping')
        .select('id')
        .eq('mre_checklist_id', selectedMre.id)
        .eq('light_checklist_id', lightId)
        .single();

      if (existing) {
        alert('This Light is already mapped to this MRE.');
        return;
      }

      // Insert new mapping
      const { error } = await supabase
        .from('mre_light_mapping')
        .insert([{
          mre_checklist_id: selectedMre.id,
          light_checklist_id: lightId
        }]);

      if (error) throw error;

      // Refresh Lights list
      await fetchLights(selectedMre.id);
      setSelectedNewLight('');
      alert(matchedLight ? 'Light mapped successfully!' : 'Light created and mapped successfully!');
    } catch (error) {
      console.error('Error adding light:', error);
      alert('Failed to add light. Please try again.');
    }
  };

  const handleDeleteLight = async (lightId) => {
    if (!selectedMre || !window.confirm('Are you sure you want to remove this Light from this MRE?')) return;

    try {
      setDeletingLight(lightId);
      const { error } = await supabase
        .from('mre_light_mapping')
        .delete()
        .eq('mre_checklist_id', selectedMre.id)
        .eq('light_checklist_id', lightId);

      if (error) throw error;

      // Refresh Lights list
      await fetchLights(selectedMre.id);
      alert('Light removed successfully!');
    } catch (error) {
      console.error('Error deleting light:', error);
      alert('Failed to remove light. Please try again.');
    } finally {
      setDeletingLight(null);
    }
  };

  const handleOpenAdrQuestionModal = () => {
    setShowAdrQuestionModal(true);
  };

  const handleCloseAdrQuestionModal = () => {
    setShowAdrQuestionModal(false);
    setSelectedNewQuestion('');
  };

  const handleOpenLightQuestionModal = () => {
    setShowLightQuestionModal(true);
  };

  const handleCloseLightQuestionModal = () => {
    setShowLightQuestionModal(false);
    setSelectedNewLightQuestion('');
  };

  // Question rows belong to an ADR and are shared across MREs: identical text is reused rather
  // than duplicated, then mapped to the current MRE so it can carry its own placeholder values.
  const handleAddAdrQuestion = async () => {
    if (!selectedAdr || !selectedMre || !selectedNewQuestion.trim()) return;

    try {
      const questionInput = selectedNewQuestion.trim();
      const now = new Date().toISOString();

      // First, check if question already exists for this ADR
      const { data: existingQuestions, error: fetchError } = await supabase
        .from('adr_questions')
        .select('id, questions')
        .eq('adr_checklist_id', selectedAdr.id);

      if (fetchError) throw fetchError;

      // Find if question text already exists (case-insensitive)
      const normalizedInput = questionInput.toLowerCase().trim();
      let questionId;
      const existingQuestion = existingQuestions?.find(q => 
        q.questions.toLowerCase().trim() === normalizedInput
      );

      if (existingQuestion) {
        questionId = existingQuestion.id;
      } else {
        // Insert new question if it doesn't exist
        const { data: newQuestion, error: insertError } = await supabase
          .from('adr_questions')
          .insert([{
            adr_checklist_id: selectedAdr.id,
            questions: questionInput,
            created_at: now,
            updated_at: now
          }])
          .select('id')
          .single();

        if (insertError) throw insertError;
        questionId = newQuestion.id;
      }

      // Check if mapping already exists
      const { data: existingMapping } = await supabase
        .from('mre_adr_questions_mapping')
        .select('id')
        .eq('mre_id', selectedMre.id)
        .eq('question_id', questionId)
        .single();

      if (existingMapping) {
        alert('This question is already mapped to this MRE.');
        return;
      }

      // Create mapping
      const { error: mappingError } = await supabase
        .from('mre_adr_questions_mapping')
        .insert([{
          mre_id: selectedMre.id,
          question_id: questionId,
          created_at: now
        }]);

      if (mappingError) throw mappingError;

      // Refresh questions list
      await fetchQuestions(selectedAdr.id);
      setSelectedNewQuestion('');
      alert(existingQuestion ? 'Question mapped successfully!' : 'Question created and mapped successfully!');
    } catch (error) {
      console.error('Error adding question:', error);
      alert('Failed to add question. Please try again.');
    }
  };

  // Unmaps the question from this MRE only; the adr_questions row survives for other MREs.
  const handleDeleteAdrQuestion = async (questionId) => {
    if (!selectedMre || !window.confirm('Are you sure you want to remove this question from this MRE?')) return;

    try {
      setDeletingQuestion(questionId);
      const { error } = await supabase
        .from('mre_adr_questions_mapping')
        .delete()
        .eq('mre_id', selectedMre.id)
        .eq('question_id', questionId);

      if (error) throw error;

      // Refresh questions list
      await fetchQuestions(selectedAdr.id);
      alert('Question removed from MRE successfully!');
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to remove question. Please try again.');
    } finally {
      setDeletingQuestion(null);
    }
  };

  // Pulls <TOKEN> placeholders out of question text; MAKE and MODEL CODE are excluded because
  // they are substituted from the vehicle's own data rather than entered per MRE.
  const extractPlaceholders = (questionText) => {
    const placeholderRegex = /<([^>]+)>/g;
    const matches = [];
    let match;
    
    while ((match = placeholderRegex.exec(questionText)) !== null) {
      const placeholder = match[1];
      // Ignore MAKE and MODEL CODE
      if (placeholder !== 'MAKE' && placeholder !== 'MODEL CODE') {
        matches.push(placeholder);
      }
    }
    
    return matches;
  };

  const handleEditAdrQuestion = async (question) => {
    setEditingQuestionId(question.id);
    setEditedQuestionText(question.questions);
    
    const placeholders = extractPlaceholders(question.questions);
    
    // Fetch existing placeholder values from DB for this specific MRE-question combination
    // This allows the same question to have different placeholder values in different MREs
    // Example: "Check <COMPONENT>" can show "Check Engine" in MRE001 and "Check Transmission" in MRE002
    if (selectedMre) {
      try {
        const { data, error } = await supabase
          .from('section_17_part_numbers')
          .select('vars')
          .eq('question_id', question.id)
          .eq('mre_id', selectedMre.id)
          .single();

        // PGRST116 just means "no row yet" for this MRE-question pair, which is the normal
        // first-edit case; anything else is a real failure worth logging.
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching existing values:', error);
        }

        // vars is a JSON column keyed by placeholder name; only placeholders still present in the
        // question text are surfaced, so values left over from removed tokens are ignored.
        const existingVars = data?.vars || {};
        const initialValues = {};
        placeholders.forEach(placeholder => {
          initialValues[placeholder] = existingVars[placeholder] || '';
        });
        setPlaceholderValues(initialValues);
      } catch (error) {
        console.error('Error fetching values:', error);
        const initialValues = {};
        placeholders.forEach(placeholder => {
          initialValues[placeholder] = '';
        });
        setPlaceholderValues(initialValues);
      }
    } else {
      const initialValues = {};
      placeholders.forEach(placeholder => {
        initialValues[placeholder] = '';
      });
      setPlaceholderValues(initialValues);
    }
  };

  const handlePlaceholderChange = (placeholder, value) => {
    setPlaceholderValues(prev => ({
      ...prev,
      [placeholder]: value
    }));
  };

  const handleSavePlaceholders = async (questionId) => {
    if (!selectedMre) {
      alert('Please select an MRE first');
      return;
    }

    try {
      setSavingPlaceholders(true);

      // Save placeholder values for this specific MRE-question combination
      // This allows the same question to have different values in different MREs
      // Example: Same question "Check <COMPONENT>" can save COMPONENT=Engine for MRE001
      // and COMPONENT=Transmission for MRE002
      // Check if record exists
      const { data: existing, error: fetchError } = await supabase
        .from('section_17_part_numbers')
        .select('id')
        .eq('question_id', questionId)
        .eq('mre_id', selectedMre.id)
        .single();

      // A missing row (PGRST116) is expected and routes to the insert branch below.
      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existing) {
        // Update existing record for this MRE-question combination
        const { error: updateError } = await supabase
          .from('section_17_part_numbers')
          .update({
            vars: placeholderValues,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (updateError) throw updateError;
      } else {
        // Insert new record for this MRE-question combination
        const { error: insertError } = await supabase
          .from('section_17_part_numbers')
          .insert({
            question_id: questionId,
            mre_id: selectedMre.id,
            vars: placeholderValues
          });

        if (insertError) throw insertError;
      }

      alert('Placeholder values saved successfully!');
    } catch (error) {
      console.error('Error saving placeholder values:', error);
      alert('Error saving placeholder values: ' + error.message);
    } finally {
      setSavingPlaceholders(false);
    }
  };

  const handleCancelEditAdrQuestion = () => {
    setEditingQuestionId(null);
    setEditedQuestionText('');
    setPlaceholderValues({});
  };

  const handleSaveAdrQuestion = async (questionId) => {
    if (!editedQuestionText.trim()) {
      alert('Question text cannot be empty.');
      return;
    }

    try {
      setSavingQuestion(true);
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('adr_questions')
        .update({
          questions: editedQuestionText.trim(),
          updated_at: now
        })
        .eq('id', questionId);

      if (error) throw error;

      // Re-extract placeholders with the new question text
      // Editing the text can add or drop tokens; values for surviving tokens are carried over
      // and must still be saved separately via handleSavePlaceholders.
      const newPlaceholders = extractPlaceholders(editedQuestionText);
      const initialValues = {};
      newPlaceholders.forEach(placeholder => {
        initialValues[placeholder] = placeholderValues[placeholder] || '';
      });
      setPlaceholderValues(initialValues);

      // Refresh questions list
      await fetchQuestions(selectedAdr.id);
      alert('Question updated successfully!');
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Failed to update question. Please try again.');
    } finally {
      setSavingQuestion(false);
    }
  };

  // Unlike ADR questions, light questions are always inserted fresh (no reuse of matching text),
  // then mapped to the current MRE.
  const handleAddLightQuestion = async () => {
    if (!selectedLight || !selectedNewLightQuestion.trim() || !selectedMre) return;

    try {
      const questionInput = selectedNewLightQuestion.trim();
      const now = new Date().toISOString();

      // Insert new question
      const { data: newQuestion, error: questionError } = await supabase
        .from('light_questions')
        .insert([{
          light_checklist_id: selectedLight.id,
          question_text: questionInput,
          created_at: now,
          updated_at: now
        }])
        .select('id')
        .single();

      if (questionError) throw questionError;

      // Create mapping entry for this MRE
      const { error: mappingError } = await supabase
        .from('mre_light_questions_mapping')
        .insert([{
          mre_id: selectedMre.id,
          question_id: newQuestion.id
        }]);

      if (mappingError) throw mappingError;

      // Refresh light questions list
      await fetchLightQuestions(selectedLight.id);
      setSelectedNewLightQuestion('');
      alert('Light question added successfully!');
    } catch (error) {
      console.error('Error adding light question:', error);
      alert('Failed to add light question. Please try again.');
    }
  };

  const handleDeleteLightQuestion = async (questionId) => {
    if (!selectedMre || !window.confirm('Are you sure you want to remove this light question from this MRE?')) return;

    try {
      setDeletingLightQuestion(questionId);
      
      // Delete the mapping entry for this MRE
      const { error } = await supabase
        .from('mre_light_questions_mapping')
        .delete()
        .eq('mre_id', selectedMre.id)
        .eq('question_id', questionId);

      if (error) throw error;

      // Refresh light questions list
      await fetchLightQuestions(selectedLight.id);
      alert('Light question removed from this MRE successfully!');
    } catch (error) {
      console.error('Error deleting light question:', error);
      alert('Failed to delete light question. Please try again.');
    } finally {
      setDeletingLightQuestion(null);
    }
  };

  const handleEditLightQuestion = async (question) => {
    setEditingLightQuestionId(question.id);
    setEditedLightQuestionText(question.question_text);
    
    const placeholders = extractPlaceholders(question.question_text);
    
    // Fetch existing placeholder values from DB for this specific MRE-light-question combination
    // This allows the same question to have different placeholder values in different MREs
    // Example: "Check <COMPONENT>" can show "Check Engine" in MRE001 and "Check Transmission" in MRE002
    if (selectedMre) {
      try {
        const { data, error } = await supabase
          .from('section_17_part_numbers')
          .select('vars')
          .eq('light_question_id', question.id)
          .eq('mre_id', selectedMre.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching existing values:', error);
        }

        const existingVars = data?.vars || {};
        const initialValues = {};
        placeholders.forEach(placeholder => {
          initialValues[placeholder] = existingVars[placeholder] || '';
        });
        setLightPlaceholderValues(initialValues);
      } catch (error) {
        console.error('Error fetching values:', error);
        const initialValues = {};
        placeholders.forEach(placeholder => {
          initialValues[placeholder] = '';
        });
        setLightPlaceholderValues(initialValues);
      }
    } else {
      const initialValues = {};
      placeholders.forEach(placeholder => {
        initialValues[placeholder] = '';
      });
      setLightPlaceholderValues(initialValues);
    }
  };

  const handleCancelEditLightQuestion = () => {
    setEditingLightQuestionId(null);
    setEditedLightQuestionText('');
    setLightPlaceholderValues({});
  };

  const handleSaveLightQuestion = async (questionId) => {
    if (!editedLightQuestionText.trim()) {
      alert('Question text cannot be empty.');
      return;
    }

    try {
      setSavingLightQuestion(true);
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('light_questions')
        .update({
          question_text: editedLightQuestionText.trim(),
          updated_at: now
        })
        .eq('id', questionId);

      if (error) throw error;

      // Re-extract placeholders with the new question text
      const newPlaceholders = extractPlaceholders(editedLightQuestionText);
      const initialValues = {};
      newPlaceholders.forEach(placeholder => {
        initialValues[placeholder] = lightPlaceholderValues[placeholder] || '';
      });
      setLightPlaceholderValues(initialValues);

      // Refresh light questions list
      await fetchLightQuestions(selectedLight.id);
      alert('Light question updated successfully!');
    } catch (error) {
      console.error('Error updating light question:', error);
      alert('Failed to update light question. Please try again.');
    } finally {
      setSavingLightQuestion(false);
    }
  };

  const handleLightPlaceholderChange = (placeholder, value) => {
    setLightPlaceholderValues(prev => ({
      ...prev,
      [placeholder]: value
    }));
  };

  const handleSaveLightPlaceholders = async (questionId) => {
    if (!selectedMre) {
      alert('Please select an MRE first');
      return;
    }

    try {
      setSavingLightPlaceholders(true);

      // Save placeholder values for this specific MRE-light-question combination
      // This allows the same question to have different values in different MREs
      // Example: Same question "Check <COMPONENT>" can save COMPONENT=Engine for MRE001
      // and COMPONENT=Transmission for MRE002
      // Check if record exists
      const { data: existing, error: fetchError } = await supabase
        .from('section_17_part_numbers')
        .select('id')
        .eq('light_question_id', questionId)
        .eq('mre_id', selectedMre.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (existing) {
        // Update existing record for this MRE-light-question combination
        const { error: updateError } = await supabase
          .from('section_17_part_numbers')
          .update({
            vars: lightPlaceholderValues,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (updateError) throw updateError;
      } else {
        // Insert new record for this MRE-light-question combination
        // When light_question_id is present, question_id is null
        const { error: insertError } = await supabase
          .from('section_17_part_numbers')
          .insert({
            question_id: null,
            light_question_id: questionId,
            mre_id: selectedMre.id,
            vars: lightPlaceholderValues
          });

        if (insertError) throw insertError;
      }

      alert('Placeholder values saved successfully!');
    } catch (error) {
      console.error('Error saving placeholder values:', error);
      alert('Error saving placeholder values: ' + error.message);
    } finally {
      setSavingLightPlaceholders(false);
    }
  };

  const fetchLightQuestions = async (lightId) => {
    // Nothing to load until an MRE is chosen: the mapping table is queried by mre_id.
    if (!selectedMre) return;
    
    try {
      setLoadingLightQuestions(true);
      
      // Fetch questions that are mapped to this MRE
      // This allows the same question to exist in different MREs with different placeholder values
      // Each MRE-question combination can have unique values stored in section_17_part_numbers
      const { data, error } = await supabase
        .from('mre_light_questions_mapping')
        .select(`
          question_id,
          light_questions(
            id,
            question_text,
            light_checklist_id
          )
        `)
        .eq('mre_id', selectedMre.id);

      if (error) throw error;
      
      // Filter questions for the selected light and flatten the structure
      // This ensures same question can exist in different MREs with different placeholder values
      const filteredQuestions = data
        ?.filter(item => item.light_questions?.light_checklist_id === lightId)
        .map(item => ({
          id: item.light_questions.id,
          question_text: item.light_questions.question_text
        })) || [];
      
      setLightQuestions(filteredQuestions);
    } catch (error) {
      console.error('Error fetching light questions:', error);
    } finally {
      setLoadingLightQuestions(false);
    }
  };

  // Loads the single photo held for the current MRE + question "slot". sec_17_photos stores both
  // ADR and light photos, so the unused id column must be null-checked to hit the right row.
  const fetchExistingPhoto = async () => {
    try {
      let query = supabase
        .from('sec_17_photos')
        .select('id, photo_url, created_at')
        .eq('mre_id', selectedMre.id);

      // Add condition based on category
      if (category === 'adr' && selectedQuestion) {
        query = query.eq('question_id', selectedQuestion.id).is('light_question_id', null);
      } else if (category === 'light' && selectedLightQuestion) {
        query = query.eq('light_question_id', selectedLightQuestion.id).is('question_id', null);
      } else {
        return;
      }

      const { data, error } = await query.maybeSingle();

      // No stored photo yet is a normal empty slot.
      if (error) {
        console.error('Error fetching existing photo:', error);
      }

      if (data && data.photo_url) {
        const questionId = category === 'adr' ? selectedQuestion.id : selectedLightQuestion.id;
        const fileExtension = data.photo_url.split('.').pop().split('?')[0];
        const storagePath =
          toReportsStoragePath(data.photo_url) ||
          buildSection17FilePath(selectedMre.mre_id, category, questionId, fileExtension);

        data.storage_path = storagePath;
        try {
          // The reports bucket is private, so the preview needs a short-lived signed URL.
          data.photo_url = await createReportsSignedUrl(storagePath);
        } catch (signError) {
          console.error('Error creating signed URL for section 17 photo:', signError);
          data.photo_url = null;
        }
      }

      setUploadedPhoto(data || null);
    } catch (error) {
      console.error('Error fetching existing photo:', error);
      setUploadedPhoto(null);
    }
  };

  // Returns a user-facing error message, or null when the file is acceptable.
  const validateFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return 'Only image files (JPEG, PNG, GIF, WebP) are allowed.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError(null);

    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        // Clearing the input is required, otherwise re-picking the same file fires no change event.
        e.target.value = '';
        return;
      }
      setPhoto(file);
    }
  };

  const removePhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Deletes the stored object and its sec_17_photos row. Prefer the path resolved when the
  // photo was loaded; otherwise rebuild it from the MRE code and question id.
  const handleDeletePhoto = async () => {
    if (!uploadedPhoto || !selectedMre) return;

    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      let filePath = uploadedPhoto.storage_path || toReportsStoragePath(uploadedPhoto.photo_url);

      if (!filePath) {
        const fileExtension = (uploadedPhoto.photo_url || '').split('.').pop().split('?')[0];
        let questionId;
        if (category === 'adr' && selectedQuestion) {
          questionId = selectedQuestion.id;
        } else if (category === 'light' && selectedLightQuestion) {
          questionId = selectedLightQuestion.id;
        } else {
          throw new Error('Invalid category or question selection');
        }
        filePath = buildSection17FilePath(selectedMre.mre_id, category, questionId, fileExtension);
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(REPORTS_BUCKET)
        .remove([filePath]);

      if (storageError) {
        console.error('Storage delete error:', storageError);
        // Continue even if storage delete fails
        // A missing or differently-named object must not stop the DB row from being removed,
        // otherwise the slot stays occupied and no new photo can be uploaded.
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('sec_17_photos')
        .delete()
        .eq('id', uploadedPhoto.id);

      if (dbError) throw dbError;

      // Clear the uploaded photo state to remove from UI
      setUploadedPhoto(null);
      setPhoto(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccess(false);
      
    } catch (error) {
      console.error('Delete error:', error);
      setError(error.message || 'Failed to delete photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Uploads the selected photo to the reports bucket and records its storage path against the
  // MRE + question slot. Guards run in cascade order so the message names the first missing step.
  const handleUpload = async () => {
    if (!photo) {
      setError('Please select a photo to upload.');
      return;
    }

    if (!selectedMre) {
      setError('Please select an MRE.');
      return;
    }

    if (!category) {
      setError('Please select a category (ADR or Light).');
      return;
    }

    if (category === 'adr' && !selectedQuestion) {
      setError('Please select an ADR question.');
      return;
    }

    if (category === 'light' && !selectedLightQuestion) {
      setError('Please select a Light question.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Get file extension
      const fileExtension = photo.name.split('.').pop();
      
      const questionId = category === 'adr' ? selectedQuestion.id : selectedLightQuestion.id;
      const folderPath = `${selectedMre.mre_id}/${SECTION_17_FOLDER}`;
      // Deterministic names (q_ for ADR questions, lq_ for light questions) keep exactly one
      // photo per question slot, so a re-upload replaces the previous file instead of piling up.
      const filePath = buildSection17FilePath(
        selectedMre.mre_id,
        category,
        questionId,
        fileExtension
      );
      const fileName = filePath.split('/').pop();

      // Check if file already exists and delete it
      const { data: existingFiles } = await supabase.storage
        .from(REPORTS_BUCKET)
        .list(folderPath);

      if (existingFiles) {
        const existingFile = existingFiles.find(f => f.name === fileName);
        if (existingFile) {
          await supabase.storage
            .from(REPORTS_BUCKET)
            .remove([filePath]);
        }
      }

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(REPORTS_BUCKET)
        .upload(filePath, photo, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Persist the storage path. Display uses a signed URL created in fetchExistingPhoto.
      const photoUrl = filePath;

      // Check if entry already exists based on category
      let existingEntryQuery = supabase
        .from('sec_17_photos')
        .select('id')
        .eq('mre_id', selectedMre.id);

      if (category === 'adr') {
        existingEntryQuery = existingEntryQuery
          .eq('question_id', selectedQuestion.id)
          .is('light_question_id', null);
      } else {
        existingEntryQuery = existingEntryQuery
          .eq('light_question_id', selectedLightQuestion.id)
          .is('question_id', null);
      }

      const { data: existingEntry } = await existingEntryQuery.maybeSingle();

      if (existingEntry) {
        // Update existing entry
        const { error: updateError } = await supabase
          .from('sec_17_photos')
          .update({
            photo_url: photoUrl,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingEntry.id);

        if (updateError) throw updateError;
      } else {
        // Insert new entry
        const insertData = {
          mre_id: selectedMre.id,
          photo_url: photoUrl
        };

        if (category === 'adr') {
          insertData.question_id = selectedQuestion.id;
          insertData.light_question_id = null;
        } else {
          insertData.light_question_id = selectedLightQuestion.id;
          insertData.question_id = null;
        }

        const { error: insertError } = await supabase
          .from('sec_17_photos')
          .insert(insertData);

        if (insertError) throw insertError;
      }

      setSuccess(true);
      
      // Fetch the updated photo
      await fetchExistingPhoto();
      
      setTimeout(() => {
        setPhoto(null);
        setSuccess(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 3000);

    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-12">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-indigo-600 mb-4">
              Section 17 - Management System
            </h1>
            <p className="text-lg text-gray-500 max-w-3xl mx-auto">
              Manage ADR and Light No, update questions, and upload photos for Section 17.
            </p>
          </div>
          
          <div className="border-t border-gray-200 pt-8">
            {/* MRE Dropdown */}
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                options={mres}
                getOptionLabel={(option) => option.mre_id || ''}
                value={selectedMre}
                onChange={(event, newValue) => setSelectedMre(newValue)}
                loading={loadingMres}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select MRE"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMres ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                fullWidth
              />
            </Box>

            {/* Category Selection Radio Buttons */}
            {selectedMre && (
              <Box sx={{ mb: 3 }}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Category
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      value="adr"
                      checked={category === 'adr'}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">ADR</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="category"
                      value="light"
                      checked={category === 'light'}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Light</span>
                  </label>
                </div>
              </Box>
            )}

            {/* ADR Dropdown */}
            {category === 'adr' && (
              <Box sx={{ mb: 3 }}>
                <div className="flex gap-2">
                  <Autocomplete
                    options={adrs}
                    getOptionLabel={(option) => option.adr_no || ''}
                    value={selectedAdr}
                    onChange={(event, newValue) => setSelectedAdr(newValue)}
                    loading={loadingAdrs}
                    disabled={!selectedMre}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select ADR"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingAdrs ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleOpenAdrModal}
                    disabled={!selectedMre}
                    sx={{ 
                      minWidth: '140px',
                      height: '56px',
                      textTransform: 'none',
                      fontSize: '15px'
                    }}
                  >
                    Manage ADRs
                  </Button>
                </div>
              </Box>
            )}

            {/* Light Dropdown */}
            {category === 'light' && (
              <Box sx={{ mb: 3 }}>
                <div className="flex gap-2">
                  <Autocomplete
                    options={lights}
                    getOptionLabel={(option) => option.light_model || ''}
                    value={selectedLight}
                    onChange={(event, newValue) => setSelectedLight(newValue)}
                    loading={loadingLights}
                    disabled={!selectedMre}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Light"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingLights ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleOpenLightModal}
                    disabled={!selectedMre}
                    sx={{ 
                      minWidth: '140px',
                      height: '56px',
                      textTransform: 'none',
                      fontSize: '15px'
                    }}
                  >
                    Manage Lights
                  </Button>
                </div>
              </Box>
            )}

            {/* ADR Questions Dropdown */}
            {category === 'adr' && selectedAdr && (
              <Box sx={{ mb: 3 }}>
                <div className="flex gap-2">
                  <Autocomplete
                    options={questions}
                    getOptionLabel={(option) => option.questions || ''}
                    value={selectedQuestion}
                    onChange={(event, newValue) => setSelectedQuestion(newValue)}
                    loading={loadingQuestions}
                    disabled={!selectedAdr}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Question"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingQuestions ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleOpenAdrQuestionModal}
                    disabled={!selectedAdr}
                    sx={{ 
                      minWidth: '180px',
                      height: '56px',
                      textTransform: 'none',
                      fontSize: '15px'
                    }}
                  >
                    Manage Questions
                  </Button>
                </div>
              </Box>
            )}

            {/* Light Questions Dropdown */}
            {category === 'light' && selectedLight && (
              <Box sx={{ mb: 3 }}>
                <div className="flex gap-2">
                  <Autocomplete
                    options={lightQuestions}
                    getOptionLabel={(option) => option.question_text || ''}
                    value={selectedLightQuestion}
                    onChange={(event, newValue) => setSelectedLightQuestion(newValue)}
                    loading={loadingLightQuestions}
                    disabled={!selectedLight}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Light Question"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingLightQuestions ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleOpenLightQuestionModal}
                    disabled={!selectedLight}
                    sx={{ 
                      minWidth: '180px',
                      height: '56px',
                      textTransform: 'none',
                      fontSize: '15px'
                    }}
                  >
                    Manage Questions
                  </Button>
                </div>
              </Box>
            )}

            {/* Uploaded Photo Preview Section */}
            {((category === 'adr' && selectedQuestion) || (category === 'light' && selectedLightQuestion)) && uploadedPhoto && (
              <div className="mt-8 border-t border-gray-200 pt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Current Photo</h3>
                <div className="bg-gray-50 rounded-lg p-6 mb-8">
                  <div className="flex flex-col items-center">
                    {uploadedPhoto.photo_url && (
                      <div className="mb-4 flex justify-center">
                        <img
                          src={uploadedPhoto.photo_url}
                          alt="Section 17 Photo"
                          className="max-h-40 h-auto rounded-lg shadow-md object-contain"
                          key={uploadedPhoto.photo_url}
                        />
                      </div>
                    )}
                    <div className="text-sm text-gray-500 mb-4">
                      Uploaded on: {new Date(uploadedPhoto.created_at).toLocaleString()}
                    </div>
                    <button
                      onClick={handleDeletePhoto}
                      disabled={deleting}
                      className="px-6 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {deleting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <XMarkIcon className="w-4 h-4 mr-2" />
                          Delete Photo
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Photo Upload Section */}
            {((category === 'adr' && selectedQuestion) || (category === 'light' && selectedLightQuestion)) && !uploadedPhoto && (
              <div className="mt-8 border-t border-gray-200 pt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Upload Photo</h3>
                
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Photo uploaded successfully!
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Photo
                  </label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <PhotoIcon className="w-10 h-10 mb-2 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                          <span className="font-semibold">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-gray-500">Images only (JPEG, PNG, GIF, WebP, max 25MB)</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                </div>

                {photo && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Selected Photo Preview</h4>
                    <div className="border border-gray-300 rounded-lg p-4 bg-white shadow-md">
                      {/* Image Preview */}
                      <div className="mb-4 flex justify-center">
                        <img 
                          src={URL.createObjectURL(photo)} 
                          alt="Preview" 
                          className="max-w-full max-h-96 rounded-lg object-contain"
                        />
                      </div>
                      
                      {/* File Info */}
                      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <PhotoIcon className="w-8 h-8 text-gray-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {photo.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(photo.size / (1024 * 1024)).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={removePhoto}
                          disabled={uploading}
                          className="ml-2 flex-shrink-0 text-red-500 hover:text-red-700 disabled:opacity-50"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={removePhoto}
                    disabled={uploading || !photo}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !photo}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {uploading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                        Upload Photo
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ADR Management Modal */}
      <Dialog 
        open={showAdrModal} 
        onClose={handleCloseAdrModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <span>Manage ADRs for {selectedMre?.mre_id}</span>
            <IconButton onClick={handleCloseAdrModal} size="small" sx={{ color: 'white' }}>
              <XMarkIcon className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {/* Current ADRs List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current ADRs</h3>
            {loadingAdrs ? (
              <div className="flex justify-center py-4">
                <CircularProgress size={30} />
              </div>
            ) : adrs.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No ADRs mapped to this MRE</p>
            ) : (
              <List className="border rounded-lg">
                {adrs.map((adr, index) => (
                  <React.Fragment key={adr.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteAdr(adr.id)}
                          disabled={deletingAdr === adr.id}
                          sx={{ color: 'red' }}
                        >
                          {deletingAdr === adr.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <TrashIcon className="w-5 h-5" />
                          )}
                        </IconButton>
                      }
                    >
                      <ListItemText primary={adr.adr_no} />
                    </ListItem>
                    {index < adrs.length - 1 && <div className="border-b" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </div>

          {/* Add New ADR */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Add New ADR</h3>
            <div className="flex gap-2">
              <TextField
                label="Enter ADR Number"
                variant="outlined"
                size="small"
                value={selectedNewAdr}
                onChange={(e) => setSelectedNewAdr(e.target.value)}
                placeholder="e.g.,XX/XX, 01/00"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleAddAdr}
                disabled={!selectedNewAdr.trim()}
                startIcon={<PlusIcon className="w-4 h-4" />}
                sx={{ textTransform: 'none' }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleCloseAdrModal} 
            variant="contained"
            size="large"
            sx={{ 
              textTransform: 'none',
              minWidth: '120px',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Light Management Modal */}
      <Dialog 
        open={showLightModal} 
        onClose={handleCloseLightModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <span>Manage Lights for {selectedMre?.mre_id}</span>
            <IconButton onClick={handleCloseLightModal} size="small" sx={{ color: 'white' }}>
              <XMarkIcon className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {/* Current Lights List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Lights</h3>
            {loadingLights ? (
              <div className="flex justify-center py-4">
                <CircularProgress size={30} />
              </div>
            ) : lights.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No Lights mapped to this MRE</p>
            ) : (
              <List className="border rounded-lg">
                {lights.map((light, index) => (
                  <React.Fragment key={light.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          onClick={() => handleDeleteLight(light.id)}
                          disabled={deletingLight === light.id}
                          sx={{ color: 'red' }}
                        >
                          {deletingLight === light.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <TrashIcon className="w-5 h-5" />
                          )}
                        </IconButton>
                      }
                    >
                      <ListItemText primary={light.light_model} />
                    </ListItem>
                    {index < lights.length - 1 && <div className="border-b" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </div>

          {/* Add New Light */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Add New Light</h3>
            <div className="flex gap-2">
              <TextField
                label="Enter Light Model"
                variant="outlined"
                size="small"
                value={selectedNewLight}
                onChange={(e) => setSelectedNewLight(e.target.value)}
                placeholder="e.g., LED-100"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleAddLight}
                disabled={!selectedNewLight.trim()}
                startIcon={<PlusIcon className="w-4 h-4" />}
                sx={{ textTransform: 'none' }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleCloseLightModal} 
            variant="contained"
            size="large"
            sx={{ 
              textTransform: 'none',
              minWidth: '120px',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADR Question Management Modal */}
      <Dialog 
        open={showAdrQuestionModal} 
        onClose={handleCloseAdrQuestionModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <span>Manage Questions for {selectedAdr?.adr_no}</span>
            <IconButton onClick={handleCloseAdrQuestionModal} size="small" sx={{ color: 'white' }}>
              <XMarkIcon className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {/* Current Questions List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Questions</h3>
            {loadingQuestions ? (
              <div className="flex justify-center py-4">
                <CircularProgress size={30} />
              </div>
            ) : questions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No questions found for this ADR</p>
            ) : (
              <List className="border rounded-lg">
                {questions.map((question, index) => (
                  <React.Fragment key={question.id}>
                    <ListItem
                      sx={{ 
                        flexDirection: 'column', 
                        alignItems: 'stretch',
                        padding: '12px 16px'
                      }}
                    >
                      {editingQuestionId === question.id ? (
                        <div className="flex flex-col gap-3 w-full">
                          <TextField
                            value={editedQuestionText}
                            onChange={(e) => setEditedQuestionText(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Question Text"
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              onClick={handleCancelEditAdrQuestion}
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleSaveAdrQuestion(question.id)}
                              disabled={savingQuestion}
                              variant="contained"
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              {savingQuestion ? 'Saving...' : 'Update Question'}
                            </Button>
                          </div>
                          
                          {/* Placeholder Values Section */}
                          {extractPlaceholders(editedQuestionText).length > 0 && (
                            <Box 
                              sx={{ 
                                mt: 3, 
                                p: 3, 
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                border: '2px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 24,
                                    bgcolor: 'primary.main',
                                    borderRadius: 1,
                                    mr: 2
                                  }}
                                />
                                <h4 className="text-base font-bold text-gray-800">Part No Values for this MRE</h4>
                              </Box>
                              <p className="text-xs text-gray-500 mb-3">Set specific values in this question</p>
                              <Grid container spacing={2.5}>
                                {extractPlaceholders(editedQuestionText).map((placeholder, idx) => (
                                  <Grid item xs={12} key={idx}>
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2,
                                        p: 2,
                                        bgcolor: 'white',
                                        borderRadius: 1.5,
                                        border: '1px solid #e5e7eb',
                                        '&:hover': {
                                          borderColor: 'primary.main',
                                          boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)'
                                        }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          minWidth: 180,
                                          bgcolor: '#f1f5f9',
                                          py: 1,
                                          px: 2,
                                          borderRadius: 1,
                                          border: '1px solid #cbd5e1'
                                        }}
                                      >
                                        <span className="text-sm font-semibold text-gray-700">
                                          {placeholder}
                                        </span>
                                      </Box>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        value={placeholderValues[placeholder] || ''}
                                        onChange={(e) => handlePlaceholderChange(placeholder, e.target.value)}
                                        variant="outlined"
                                        placeholder="Enter value..."
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '&:hover fieldset': {
                                              borderColor: 'primary.main',
                                            },
                                          },
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                  variant="contained"
                                  size="medium"
                                  onClick={() => handleSavePlaceholders(question.id)}
                                  disabled={savingPlaceholders}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    boxShadow: 2,
                                    '&:hover': {
                                      boxShadow: 4
                                    }
                                  }}
                                >
                                  {savingPlaceholders ? (
                                    <>
                                      <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Placeholder Values'
                                  )}
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start justify-between w-full gap-2">
                          <div className="flex-1 min-w-0">
                            <p style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {question.questions}
                            </p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <IconButton
                              size="small"
                              onClick={() => handleEditAdrQuestion(question)}
                              sx={{ color: 'primary.main' }}
                            >
                              <PencilIcon className="w-5 h-5" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAdrQuestion(question.id)}
                              disabled={deletingQuestion === question.id}
                              sx={{ color: 'red' }}
                            >
                              {deletingQuestion === question.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <TrashIcon className="w-5 h-5" />
                              )}
                            </IconButton>
                          </div>
                        </div>
                      )}
                    </ListItem>
                    {index < questions.length - 1 && <div className="border-b" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </div>

          {/* Add New Question */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Add New Question</h3>
            <div className="flex gap-2">
              <TextField
                label="Enter Question"
                variant="outlined"
                size="small"
                value={selectedNewQuestion}
                onChange={(e) => setSelectedNewQuestion(e.target.value)}
                placeholder="e.g., What is the condition?"
                multiline
                rows={2}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleAddAdrQuestion}
                disabled={!selectedNewQuestion.trim()}
                startIcon={<PlusIcon className="w-4 h-4" />}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleCloseAdrQuestionModal} 
            variant="contained"
            size="large"
            sx={{ 
              textTransform: 'none',
              minWidth: '120px',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Light Question Management Modal */}
      <Dialog 
        open={showLightQuestionModal} 
        onClose={handleCloseLightQuestionModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <span>Manage Questions for {selectedLight?.light_model}</span>
            <IconButton onClick={handleCloseLightQuestionModal} size="small" sx={{ color: 'white' }}>
              <XMarkIcon className="w-5 h-5" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {/* Current Light Questions List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Questions</h3>
            {loadingLightQuestions ? (
              <div className="flex justify-center py-4">
                <CircularProgress size={30} />
              </div>
            ) : lightQuestions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No questions found for this Light</p>
            ) : (
              <List className="border rounded-lg">
                {lightQuestions.map((question, index) => (
                  <React.Fragment key={question.id}>
                    <ListItem
                      sx={{ 
                        flexDirection: 'column', 
                        alignItems: 'stretch',
                        padding: '12px 16px'
                      }}
                    >
                      {editingLightQuestionId === question.id ? (
                        <div className="flex flex-col gap-3 w-full">
                          <TextField
                            value={editedLightQuestionText}
                            onChange={(e) => setEditedLightQuestionText(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                            variant="outlined"
                            size="small"
                            label="Question Text"
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              onClick={handleCancelEditLightQuestion}
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleSaveLightQuestion(question.id)}
                              disabled={savingLightQuestion}
                              variant="contained"
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              {savingLightQuestion ? 'Saving...' : 'Update Question'}
                            </Button>
                          </div>
                          
                          {/* Placeholder Values Section */}
                          {extractPlaceholders(editedLightQuestionText).length > 0 && (
                            <Box 
                              sx={{ 
                                mt: 3, 
                                p: 3, 
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                border: '2px solid #e2e8f0',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 24,
                                    bgcolor: 'primary.main',
                                    borderRadius: 1,
                                    mr: 2
                                  }}
                                />
                                <h4 className="text-base font-bold text-gray-800">Placeholder Values for this MRE</h4>
                              </Box>
                              <p className="text-xs text-gray-500 mb-3">Set specific values for placeholders in this question</p>
                              <Grid container spacing={2.5}>
                                {extractPlaceholders(editedLightQuestionText).map((placeholder, idx) => (
                                  <Grid item xs={12} key={idx}>
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2,
                                        p: 2,
                                        bgcolor: 'white',
                                        borderRadius: 1.5,
                                        border: '1px solid #e5e7eb',
                                        '&:hover': {
                                          borderColor: 'primary.main',
                                          boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)'
                                        }
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          minWidth: 180,
                                          bgcolor: '#f1f5f9',
                                          py: 1,
                                          px: 2,
                                          borderRadius: 1,
                                          border: '1px solid #cbd5e1'
                                        }}
                                      >
                                        <span className="text-sm font-semibold text-gray-700">
                                          {placeholder}
                                        </span>
                                      </Box>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        value={lightPlaceholderValues[placeholder] || ''}
                                        onChange={(e) => handleLightPlaceholderChange(placeholder, e.target.value)}
                                        variant="outlined"
                                        placeholder="Enter value..."
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '&:hover fieldset': {
                                              borderColor: 'primary.main',
                                            },
                                          },
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                  variant="contained"
                                  size="medium"
                                  onClick={() => handleSaveLightPlaceholders(question.id)}
                                  disabled={savingLightPlaceholders}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    boxShadow: 2,
                                    '&:hover': {
                                      boxShadow: 4
                                    }
                                  }}
                                >
                                  {savingLightPlaceholders ? (
                                    <>
                                      <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                      Saving...
                                    </>
                                  ) : (
                                    'Save Placeholder Values'
                                  )}
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-start justify-between w-full gap-2">
                          <div className="flex-1 min-w-0">
                            <p style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {question.question_text}
                            </p>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <IconButton
                              size="small"
                              onClick={() => handleEditLightQuestion(question)}
                              sx={{ color: 'primary.main' }}
                            >
                              <PencilIcon className="w-5 h-5" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteLightQuestion(question.id)}
                              disabled={deletingLightQuestion === question.id}
                              sx={{ color: 'red' }}
                            >
                              {deletingLightQuestion === question.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <TrashIcon className="w-5 h-5" />
                              )}
                            </IconButton>
                          </div>
                        </div>
                      )}
                    </ListItem>
                    {index < lightQuestions.length - 1 && <div className="border-b" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </div>

          {/* Add New Light Question */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Add New Question</h3>
            <div className="flex gap-2">
              <TextField
                label="Enter Question"
                variant="outlined"
                size="small"
                value={selectedNewLightQuestion}
                onChange={(e) => setSelectedNewLightQuestion(e.target.value)}
                placeholder="e.g., What is the light specification?"
                multiline
                rows={2}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleAddLightQuestion}
                disabled={!selectedNewLightQuestion.trim()}
                startIcon={<PlusIcon className="w-4 h-4" />}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                Add
              </Button>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={handleCloseLightQuestionModal} 
            variant="contained"
            size="large"
            sx={{ 
              textTransform: 'none',
              minWidth: '120px',
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Section17PhotosUpload;
